type ClassifiableUserRequest = {
  status?: string | null;
  invitedBy?: { id?: string | null } | null;
  user?: { id?: string | null } | null;
};

/**
 * Whether a member activity user request reads as an invitation
 * (inviter → invitee) rather than as a join request (requester → approver).
 *
 * This only decides the wording/shape of the card. Who is expected to *act* on
 * a pending request comes from `resolveMemberActivityAction`, because
 * `invitedBy` stays populated even after an invitee has to re-request access
 * (TA-10740 / TA-10741).
 */
export const resolveIsInvitation = ({
  userRequest,
  authorId,
  isSender,
  meId,
}: {
  userRequest?: ClassifiableUserRequest | null;
  authorId?: string | null;
  isSender?: boolean | null;
  meId?: string | null;
}): boolean => {
  if (!userRequest) return false;
  return (
    !!userRequest.invitedBy?.id ||
    (!!userRequest.user?.id && userRequest.user.id !== authorId) ||
    (!isSender && !!userRequest.user?.id && userRequest.user.id === meId)
  );
};

/**
 * What the current viewer may do on a pending project member activity card.
 *
 * - `join`    — live invitation, viewer is the invitee: "Join Project".
 * - `request` — the invitee must ask for access first: "Request to Join".
 * - `review`  — viewer decides the request: Accept / Deny.
 * - `none`    — nothing actionable for this viewer.
 */
export type MemberActivityAction = 'join' | 'request' | 'review' | 'none';

/**
 * TA-10740 / TA-10741: nothing on the message itself says who is expected to
 * act. When an invitee registers through an expired link and re-requests
 * access, the backend keeps the same UserRequest with `invitedBy` populated and
 * `expiredAt` null, so it is byte-for-byte a live invitation — which left the
 * invitee a dead "Join Project" button and the owner no Accept/Deny.
 *
 * `canBeReviewed` (TA-10763) is the backend's per-viewer answer: true only for
 * whoever is recorded as this row's reviewer at creation time. A live
 * invitation names the invitee (self-accept is legitimate); an
 * expired-invite-turned-join-request names the inviter and project creator
 * instead, never the requester.
 *
 * Deliberately asymmetric:
 *
 * - For the **invitee** the flag is authoritative, including when it is
 *   `false` — that is the whole bug, and it is what removes "Join Project".
 * - For the **approver** the flag may only *add* Accept/Deny, never remove it.
 *   `canBeReviewed` is computed from a backend column
 *   (`user_requests.can_be_reviewed_by_ids`) that defaults to `[]` and is not
 *   backfilled, so every request already pending before the backend deploy
 *   reports `false` for everyone. Gating the approver on it would strip
 *   Accept/Deny from those rows, so the pre-existing `showAcceptDecline` path
 *   stays as a fallback.
 *
 * `canBeReviewed` is meaningful only for PROJECT and TASK rows on the backend —
 * it returns `false` for CHAT and ACCOUNT — so it is gated on
 * `userRequestableType` here rather than trusted wherever it happens to be
 * selected. That gate is load-bearing, not defensive: native's
 * `ChatMessage.tsx` routes ACCOUNT (team) invitations into `JoinRequest` (it
 * only excludes TASK and CHAT, and has no `TeamInvitationCard`), so reading a
 * blanket `false` there would send a team invitee down the `request` branch
 * and strip the action they have today. Task rows never reach `JoinRequest`;
 * they render through `TaskMemberJoinRequest`, which uses
 * `resolveTaskMemberActivityAction` below.
 */
export const resolveMemberActivityAction = ({
  status,
  isInvitee,
  isInvitation,
  canBeReviewed,
  showAcceptDecline,
  userRequestableType,
}: {
  status?: string | null;
  isInvitee?: boolean | null;
  isInvitation?: boolean | null;
  /** `undefined`/`null` when the field is absent, so it cannot change the card. */
  canBeReviewed?: boolean | null;
  showAcceptDecline?: boolean | null;
  /** Only `'PROJECT'` rows carry a meaningful `canBeReviewed`. */
  userRequestableType?: string | null;
}): MemberActivityAction => {
  if (status !== 'pending') return 'none';

  // Anything other than a project falls back to the pre-existing behaviour.
  const reviewFlag =
    userRequestableType === 'PROJECT' ? canBeReviewed : undefined;

  if (isInvitee) {
    if (reviewFlag === true) return 'join';
    if (reviewFlag === false) return 'request';
    return isInvitation ? 'join' : 'none';
  }

  if (reviewFlag === true) return 'review';
  return showAcceptDecline && !isInvitation ? 'review' : 'none';
};

/**
 * TA-10744 / TA-10745: the task-card counterpart of
 * `resolveMemberActivityAction`, for the same expired-invite registration bug
 * reached through a task (created or existing) instead of a project.
 *
 * The task card never used `resolveIsInvitation`: before this, the invitee got
 * "Join Task" and anyone else got Accept/Deny whenever the viewer was not the
 * message's sender. After an expired-invite registration that is exactly
 * backwards — the invitee sees a "Join Task" the backend rejects, and the
 * inviter, who authored the message, sees nothing.
 *
 * `canBeReviewed` covers Task rows since TA-10941 (backend #4413), and
 * TA-10902 (#4417) is what makes the pending request exist on this path. The
 * same asymmetry as the project resolver applies:
 *
 * - For the **invitee** the flag is authoritative, including `false`. It is
 *   safe here even for rows pending before the deploy: `UserRequest#joined!`
 *   refuses a Task self-accept unless the requester is named in
 *   `can_be_reviewed_by_ids`, so a "Join Task" the flag rules out would fail
 *   on the server anyway.
 * - For the **approver** the flag may only add Accept/Deny — it is what reaches
 *   the inviter, who is the message's sender — and never removes the
 *   pre-existing `!isSender` path, because un-backfilled rows report `false`.
 *
 * An invitee who is also the message's sender asked to join themselves, so
 * they wait on approval even if the backend also lists them as a reviewer.
 */
export const resolveTaskMemberActivityAction = ({
  status,
  isInvitee,
  isSender,
  canBeReviewed,
}: {
  status?: string | null;
  isInvitee?: boolean | null;
  isSender?: boolean | null;
  /** `undefined`/`null` when the field is absent, so it cannot change the card. */
  canBeReviewed?: boolean | null;
}): MemberActivityAction => {
  if (status !== 'pending') return 'none';

  if (isInvitee) {
    // A request the invitee sent themselves stays an ask, whatever the flag.
    if (isSender) return 'request';
    return canBeReviewed === false ? 'request' : 'join';
  }

  if (canBeReviewed === true) return 'review';
  return isSender ? 'none' : 'review';
};
